import { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Bounce, toast } from "react-toastify";
import api from "../api";
import "./accordoQuadro.css";
import CustomSelect from "../CustomSelect";
import useTrainerSearch, {
  trainerToOption,
} from "../hooks/useTrainerSearch";

const EditAccordoQuadro = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [accordoQuadro, setAccordoQuadro] = useState(null);
  const [selectedTrainer, setSelectedTrainer] = useState(null);
  const [form, setForm] = useState({
    trainerCodiceFiscale: "",
    dataAccordo: "",
    tipologiaAccordo: "",
    dataProssimoAggiornamento: "",
    importoDefinito: "",
  });
  const [errors, setErrors] = useState({});
  const [editMode, setEditMode] = useState(false);
  const location = useLocation();
  const returnTo = location.state?.returnTo || "/accordo-quadro";
  const returnTab = location.state?.activeTab || null;
  const tipologiaOptions = [
    { value: "Standard", label: "Standard" },
    { value: "Speciale", label: "Speciale" },
  ];
  const loadTrainerOptions = useTrainerSearch();

  useEffect(() => {
    const userRole = localStorage.getItem("userRole");

    if (userRole !== "ADMIN") {
      alert("Accedi come amministratore per modificare!");
      navigate("/login");
      localStorage.clear();
    } else {
      fetchAccordoQuadro();
    }
  }, [navigate]);

  const fetchAccordoQuadro = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/accordo-quadro/${id}`);
      setAccordoQuadro(res.data);
      setForm({
        trainerCodiceFiscale: res.data.trainerCodiceFiscale || "",
        dataAccordo: res.data.dataAccordo || "",
        tipologiaAccordo: res.data.tipologiaAccordo || "",
        dataProssimoAggiornamento: res.data.dataProssimoAggiornamento || "",
        importoDefinito: res.data.importoDefinito || "",
      });
      hydrateSelectedTrainer(res.data.trainerCodiceFiscale);
    } catch (error) {
      toast.error(error?.response?.data?.message, { transition: Bounce });
    } finally {
      setLoading(false);
    }
  };

  const hydrateSelectedTrainer = async (codiceFiscale) => {
    if (!codiceFiscale) {
      setSelectedTrainer(null);
      return;
    }
    try {
      const response = await api.get("/trainers", {
        params: {
          page: 0,
          size: 1,
          codiceFiscale_eq: codiceFiscale,
        },
      });
      const trainer =
        Array.isArray(response.data?.content) &&
        response.data.content.length > 0
          ? response.data.content[0]
          : Array.isArray(response.data) && response.data.length > 0
          ? response.data[0]
          : null;
      if (trainer) {
        setSelectedTrainer(trainerToOption(trainer));
      } else {
        setSelectedTrainer({
          value: codiceFiscale,
          label: codiceFiscale,
          trainer: { codiceFiscale },
        });
      }
    } catch (error) {
      console.error(error);
      setSelectedTrainer({
        value: codiceFiscale,
        label: codiceFiscale,
        trainer: { codiceFiscale },
      });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target || {};
    if (typeof e === "object" && e?.value !== undefined && e?.name) {
      setForm((prev) => ({
        ...prev,
        [e.name]: e.value,
      }));
      if (errors[e.name]) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[e.name];
          return newErrors;
        });
      }
      return;
    }

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validate = () => {
    const inputErrors = {};
    if (!form.trainerCodiceFiscale.trim()) {
      inputErrors.trainerCodiceFiscale =
        "Codice Fiscale Trainer non può essere vuoto!";
    }

    if (!form.dataAccordo.trim()) {
      inputErrors.dataAccordo = "Data Accordo non può essere vuota!";
    }

    if (!form.tipologiaAccordo.trim()) {
      inputErrors.tipologiaAccordo = "Tipologia Accordo non può essere vuota!";
    } else if (
      !tipologiaOptions.some((opt) => opt.value === form.tipologiaAccordo)
    ) {
      inputErrors.tipologiaAccordo = "Valore non valido per Tipologia Accordo!";
    }

    if (!form.dataProssimoAggiornamento.trim()) {
      inputErrors.dataProssimoAggiornamento =
        "Data Prossimo Aggiornamento non può essere vuota!";
    }

    setErrors(inputErrors);
    return Object.keys(inputErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    try {
      setLoading(true);
      const payload = {
        trainerCodiceFiscale: form.trainerCodiceFiscale,
        dataAccordo: form.dataAccordo,
        tipologiaAccordo: form.tipologiaAccordo,
        dataProssimoAggiornamento: form.dataProssimoAggiornamento,
        importoDefinito: form.importoDefinito,
      };

      const res = await api.put(`/accordo-quadro/${id}`, payload);
      if (res.status === 200) {
        toast.success("Accordo quadro aggiornato con successo!", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
        setAccordoQuadro(res.data);
        setEditMode(false);
        hydrateSelectedTrainer(payload.trainerCodiceFiscale);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message, { transition: Bounce });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (accordoQuadro) {
      setForm({
        trainerCodiceFiscale: accordoQuadro.trainerCodiceFiscale || "",
        dataAccordo: accordoQuadro.dataAccordo || "",
        tipologiaAccordo: accordoQuadro.tipologiaAccordo || "",
        dataProssimoAggiornamento:
          accordoQuadro.dataProssimoAggiornamento || "",
        importoDefinito: accordoQuadro.importoDefinito || "",
      });
      hydrateSelectedTrainer(accordoQuadro.trainerCodiceFiscale);
    }
    setErrors({});
    setEditMode(false);
  };

  function formatDate(dateString) {
    if (!dateString) return "";

    const date = new Date(dateString);
    if (isNaN(date)) return "";

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    return `${day}.${month}.${year}`;
  }

  const recalculateAccordo = async () => {
    try {
      setLoading(true);
      const response = await api.post(
        `/accordo-quadro/${accordoQuadro.id}/recalculate`
      );

      if (response.status === 200) {
        toast.success("Accordo quadro ricalcolato!", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
        setAccordoQuadro(response.data);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="content-page">
      {loading && (
        <div className="overlay">
          <div className="spinner"></div>
        </div>
      )}

      <div className="page-header">
        <div className="page-title">
          <i className="fa-solid fa-edit"></i>
          Modifica Accordo Quadro
        </div>
        <div className="controls">
          <button
            onClick={() => {
              if (returnTab) {
                navigate(returnTo, { state: { activeTab: returnTab } });
              } else {
                navigate(returnTo);
              }
            }}
            className="back-btn"
          >
            <i className="fa-solid fa-arrow-left"></i>
            Torna indietro
          </button>
        </div>
      </div>

      <div className="page-content accordo">
        {accordoQuadro && (
          <div className="details-card">
            <div className="card-header">
              <div className="card-title">
                <i className="fa-solid fa-file-contract"></i>
                Dettagli Accordo Quadro #{accordoQuadro.id}
              </div>
              <div className="card-actions">
                {!editMode && (
                  <button
                    className="edit-btn"
                    onClick={() => setEditMode(true)}
                  >
                    <i className="fa-solid fa-edit"></i>
                    Modifica
                  </button>
                )}
                <button className="recalc-btn" onClick={recalculateAccordo}>
                  <i className="fa-solid fa-calculator"></i>
                  Ricalcola
                </button>
              </div>
            </div>

            <div className="card-content">
              <div className="info-sections">
                <div className="info-section">
                  <div className="section-header">
                    <i className="fa-solid fa-info-circle"></i>
                    Informazioni Principali
                  </div>
                  <div className="info-grid">
                    <div className="info-item">
                      <label className="info-label">Docente:</label>
                      <div className="info-value">
                        {editMode ? (
                          <CustomSelect
                            name="trainerCodiceFiscale"
                            loadOptions={loadTrainerOptions}
                            value={selectedTrainer}
                            onChange={(selected) => {
                              setSelectedTrainer(selected);
                              const codice =
                                selected?.trainer?.codiceFiscale ||
                                selected?.value ||
                                "";
                              setForm((prev) => ({
                                ...prev,
                                trainerCodiceFiscale: codice,
                              }));
                              if (errors.trainerCodiceFiscale) {
                                setErrors((prev) => {
                                  const newErrors = { ...prev };
                                  delete newErrors.trainerCodiceFiscale;
                                  return newErrors;
                                });
                              }
                            }}
                            placeholder="Seleziona un docente"
                            className="modern-select"
                          />
                        ) : (
                          <span className="value-text">
                            {selectedTrainer?.trainer
                              ? `${selectedTrainer.trainer.nome ?? ""} ${
                                  selectedTrainer.trainer.cognome ?? ""
                                } (${
                                  selectedTrainer.trainer.codiceFiscale ?? ""
                                })`.trim()
                              : form.trainerCodiceFiscale || "-"}
                          </span>
                        )}
                        {errors.trainerCodiceFiscale && (
                          <span className="error-message">
                            <i className="fa-solid fa-exclamation-circle"></i>
                            {errors.trainerCodiceFiscale}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="info-item">
                      <label className="info-label">Data Accordo:</label>
                      <div className="info-value">
                        {editMode ? (
                          <input
                            id="dataAccordo"
                            type="datetime-local"
                            name="dataAccordo"
                            value={form.dataAccordo}
                            onChange={handleChange}
                            className="modern-input"
                          />
                        ) : (
                          <span className="value-text">
                            {formatDate(form.dataAccordo)}
                          </span>
                        )}
                        {errors.dataAccordo && (
                          <span className="error-message">
                            <i className="fa-solid fa-exclamation-circle"></i>
                            {errors.dataAccordo}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="info-item">
                      <label className="info-label">Tipologia Accordo:</label>
                      <div className="info-value">
                        {editMode ? (
                          <CustomSelect
                            name="tipologiaAccordo"
                            options={tipologiaOptions}
                            value={
                              form.tipologiaAccordo
                                ? tipologiaOptions.find(
                                    (opt) => opt.value === form.tipologiaAccordo
                                  )
                                : null
                            }
                            onChange={(selected) =>
                              handleChange({
                                target: {
                                  name: "tipologiaAccordo",
                                  value: selected ? selected.value : "",
                                },
                              })
                            }
                            placeholder="Seleziona tipologia"
                            className="modern-select"
                          />
                        ) : (
                          <span className="value-text">
                            {form.tipologiaAccordo}
                          </span>
                        )}
                        {errors.tipologiaAccordo && (
                          <span className="error-message">
                            <i className="fa-solid fa-exclamation-circle"></i>
                            {errors.tipologiaAccordo}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="info-item">
                      <label className="info-label">
                        Data Prossimo Aggiornamento:
                      </label>
                      <div className="info-value">
                        {editMode ? (
                          <input
                            id="dataProssimoAggiornamento"
                            type="date"
                            name="dataProssimoAggiornamento"
                            value={form.dataProssimoAggiornamento}
                            onChange={handleChange}
                            className="modern-input"
                          />
                        ) : (
                          <span className="value-text">
                            {formatDate(form.dataProssimoAggiornamento)}
                          </span>
                        )}
                        {errors.dataProssimoAggiornamento && (
                          <span className="error-message">
                            <i className="fa-solid fa-exclamation-circle"></i>
                            {errors.dataProssimoAggiornamento}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="info-section">
                  <div className="section-header">
                    <i className="fa-solid fa-calculator"></i>
                    Dettagli Calcolo
                  </div>
                  <div className="info-grid">
                    <div className="info-item">
                      <label className="info-label">Quota da Sistema:</label>
                      <span className="value-text">
                        {accordoQuadro.quotaDaSistema || "-"}
                      </span>
                    </div>
                    <div className="info-item">
                      <label className="info-label">Livello Accordo:</label>
                      <span className="value-text">
                        {accordoQuadro.livelloAccordo || "-"}
                      </span>
                    </div>
                    <div className="info-item">
                      <label className="info-label">Quota Oraria:</label>
                      <span className="value-text">
                        {accordoQuadro.quotaOraria || "-"}
                      </span>
                    </div>
                    <div className="info-item">
                      <label className="info-label">Importo Definito:</label>
                      <div className="info-value">
                        {editMode && form.tipologiaAccordo === "Speciale" ? (
                          <input
                            type="number"
                            name="importoDefinito"
                            value={form.importoDefinito}
                            onChange={handleChange}
                            className="modern-input"
                            placeholder="Inserisci importo"
                            step="0.01"
                            min="0"
                          />
                        ) : (
                          <span className="value-text">
                            {accordoQuadro.importoDefinito || "-"}
                          </span>
                        )}
                        {errors.importoDefinito && (
                          <span className="error-message">
                            <i className="fa-solid fa-exclamation-circle"></i>
                            {errors.importoDefinito}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="info-item">
                      <label className="info-label">Autore:</label>
                      <span className="value-text">
                        {accordoQuadro.autore || "-"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {editMode && (
                <div className="edit-actions">
                  <button className="btn-secondary" onClick={handleCancel}>
                    <i className="fa-solid fa-times"></i> Annulla
                  </button>
                  <button className="btn-primary" onClick={handleSave}>
                    <i className="fa-solid fa-save"></i> Salva Modifiche
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EditAccordoQuadro;
